"use client"
import styled, { keyframes } from "styled-components"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"

// Corrected Imports: Removed SiJava from 'si'
import {
  SiJavascript, SiReact, SiNodedotjs, SiPython, SiPhp, SiMysql, SiGit, SiWordpress, SiFigma
} from "react-icons/si"
// Added import for DiJava from Devicons
import { DiJava } from "react-icons/di";
import { FiDatabase, FiServer, FiGitMerge } from "react-icons/fi"


// --- STYLED COMPONENTS ---

const SkillsSection = styled.section`
  padding: 6rem 0;
  background: transparent;
  position: relative;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #264653, #2A9D8F);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-bottom: 5rem;
`

const SkillCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: ${({ $color }) => $color};
  }
`

const IconWrapper = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1rem;
  color: ${({ $color }) => $color};
`

const SkillName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1B1B1E;
  margin-bottom: 0.5rem;
`

const SkillLevel = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  color: ${({ $color }) => $color};
  background-color: ${({ $color }) => `${$color}1A`};
`

const scroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`

const MarqueeContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 1.5rem 0;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(5px);
  border-radius: 1rem;
  border: 1px solid rgba(38, 70, 83, 0.1);
  -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);

  &:hover > div {
    animation-play-state: paused;
  }
`

const MarqueeTrack = styled.div`
  display: flex;
  width: fit-content;
  animation: ${scroll} 30s linear infinite;
  animation-direction: ${({ $reverse }) => ($reverse ? "reverse" : "normal")};
`

const SkillTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 500;
  color: #1B1B1E;
  opacity: 0.7;
  margin: 0 2rem;
  flex-shrink: 0;
  white-space: nowrap;
`

// --- DATA ---

const coreSkills = [
  { name: "JavaScript", level: "Advanced", icon: <SiJavascript />, color: "#F7DF1E" },
  { name: "React", level: "Advanced", icon: <SiReact />, color: "#61DAFB" },
  { name: "Node.js", level: "Proficient", icon: <SiNodedotjs />, color: "#339933" },
  { name: "SQL", level: "Advanced", icon: <SiMysql />, color: "#4479A1" },
  { name: "PHP", level: "Proficient", icon: <SiPhp />, color: "#777BB4" },
  // Corrected: Using the DiJava icon from react-icons/di
  { name: "Java", level: "Intermediate", icon: <DiJava />, color: "#f89820" }, // Using official Java orange color
  { name: "Python", level: "Beginner", icon: <SiPython />, color: "#3776AB" },
];

const otherSkills = [
  { name: "Git & Version Control", icon: <SiGit /> },
  { name: "RESTful APIs", icon: <FiServer /> },
  { name: "Database Design", icon: <FiDatabase /> },
  { name: "Agile Methodology", icon: <FiGitMerge /> },
  { name: "WordPress", icon: <SiWordpress /> },
  { name: "UI/UX Principles", icon: <SiFigma /> },
  { name: "Software Testing" },
  { name: "Team Collaboration" },
  { name: "Mobile Development" },
];

// --- MAIN COMPONENT ---

const Skills = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const fadeInUp = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
  };

  return (
    <SkillsSection id="skills" ref={ref}>
      <Container>
        <SectionHeader variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <h2>
            Technical <span className="highlight">Toolkit</span>
          </h2>
          <p>The languages, frameworks, and tools I use to bring ideas to life.</p>
        </SectionHeader>

        <SkillsGrid variants={staggerContainer} initial="hidden" animate={inView ? "visible" : "hidden"}>
          {coreSkills.map((skill) => (
            <SkillCard key={skill.name} variants={fadeInUp} $color={skill.color} whileHover={{ scale: 1.05 }}>
              <div>
                <IconWrapper $color={skill.color}>{skill.icon}</IconWrapper>
                <SkillName>{skill.name}</SkillName>
              </div>
              <SkillLevel $color={skill.color}>{skill.level}</SkillLevel>
            </SkillCard>
          ))}
        </SkillsGrid>

        <MarqueeContainer variants={fadeInUp} initial="hidden" animate={inView ? "visible" : "hidden"}>
          <MarqueeTrack>
            {[...otherSkills, ...otherSkills].map((skill, index) => (
              <SkillTag key={index}>
                {skill.icon}
                {skill.name}
              </SkillTag>
            ))}
          </MarqueeTrack>
        </MarqueeContainer>
      </Container>
    </SkillsSection>
  );
};

export default Skills;